import React, { useEffect, useRef, useState, useCallback } from "react";
import { Select, Spin } from "antd";
import { searchShops } from "../../network/api";

/**
 * Server-searched outlet picker — the outlet catalog can be tens of thousands,
 * so we never load it all; we debounce-query the backend as the user types.
 *
 * Props: value, onChange, current (the already-assigned shop, to seed the label),
 *        allowClear, placeholder, style.
 */
export default function ShopSelect({ value, onChange, current, allowClear, placeholder = "Search outlets…", style }) {
  const [options, setOptions] = useState([]);
  const [fetching, setFetching] = useState(false);
  const timer = useRef(null);
  const seq = useRef(0);

  const toOption = (s) => ({ value: s._id, label: `${s.code} — ${s.title}` });

  const load = useCallback(async (search) => {
    const mySeq = ++seq.current;
    setFetching(true);
    try {
      const shops = await searchShops(search);
      if (mySeq === seq.current) {
        const opts = shops.map(toOption);
        // Keep the current selection visible even if it's not in the result set.
        if (current?._id && !opts.some((o) => o.value === current._id)) {
          opts.unshift(toOption(current));
        }
        setOptions(opts);
      }
    } catch (e) {
      if (mySeq === seq.current) setOptions(current?._id ? [toOption(current)] : []);
    } finally {
      if (mySeq === seq.current) setFetching(false);
    }
  }, [current]);

  // Initial page + seed with the current shop.
  useEffect(() => {
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?._id]);

  const onSearch = (text) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => load(text), 350);
  };

  return (
    <Select
      showSearch
      allowClear={allowClear}
      placeholder={placeholder}
      style={style || { width: "100%" }}
      value={value}
      onChange={onChange}
      onSearch={onSearch}
      filterOption={false}
      options={options}
      notFoundContent={fetching ? <Spin size="small" /> : "No matching outlets"}
    />
  );
}
